import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string; // To identify which boundary caught the error
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const boundaryName = this.props.name || 'Unknown';
    console.error(`[ErrorBoundary:${boundaryName}] Caught error:`, error);
    
    // Detailed logging for filter errors
    const errorString = error.toString();
    const stackString = error.stack || '';
    
    if (errorString.includes('filter') || 
        errorString.includes('is not a function') ||
        stackString.includes('filter')) {
      
      console.error('🚨 CRITICAL: Array method error detected!', {
        boundary: boundaryName,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
      
      // Try to extract the minified variable name
      const patterns = [
        /([a-zA-Z_$][a-zA-Z0-9_$]*)\.(filter|map|forEach|reduce|find|some|every)/,
        /Cannot read properties of (.*?) \(reading '(filter|map|forEach|reduce|find|some|every)'\)/,
        /([a-zA-Z_$][a-zA-Z0-9_$]*) is not a function/
      ];
      
      for (const pattern of patterns) {
        const match = errorString.match(pattern) || stackString.match(pattern);
        if (match) {
          console.error(`🔍 Problematic variable/expression: "${match[1]}"`);
          break;
        }
      }
      
      // Log which component might be affected
      if (errorInfo.componentStack) {
        const lines = errorInfo.componentStack.split('\n');
        const topComponent = lines[1]?.trim() || 'Unknown';
        console.error(`📍 Top component in stack: ${topComponent}`);
      }
    }
    
    this.setState({ errorInfo });
    
    // Send to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // window.gtag?.('event', 'exception', {
      //   description: error.toString(),
      //   fatal: true,
      // });
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-bg">
          <div className="text-ink text-center max-w-md">
            <h2 className="text-xl font-semibold mb-2 text-crit">Something went wrong</h2>
            <p className="text-sm mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="text-left mt-4 p-3 bg-panel rounded border border-line">
                <summary className="cursor-pointer font-medium text-sm">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap">
                  {this.state.error?.stack}
                </pre>
                {this.state.errorInfo && (
                  <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap border-t border-line pt-2">
                    Component Stack:
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}
            
            <div className="flex gap-2 justify-center mt-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-crit text-panel rounded hover:opacity-90"
              >
                Reload Page
              </button>
              <button
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                className="px-4 py-2 bg-panel text-ink border border-line rounded hover:bg-line-2"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
